/**
 * ModelValueCompare class
 * Combines ModelValue and ExcelModel properties for comparison operations
 * between database configuration and Excel template data
 */

import { TreeDataItemCheckHighlighted } from "../../../modules/fwcore/models/tree-data-item";
import { ExcelModel } from "./excel-model";
import { ModelValue } from "./model-value";

export class ModelValueCompare extends TreeDataItemCheckHighlighted {

  /** Existing varName in database model */
  varName?: string | null;

  /** model value from database*/
  mv?: ModelValue | null;

  /** model row config from excel */
  mvExcel?: ExcelModel | null;

  // -------------- State properties --------------

  isInDbOnly: boolean = true;
  isInExcelOnly: boolean = true;

  hasDifferences: boolean = false;

  hasChangesToSave: boolean = false;

  // -------------- Properties to update  --------------

  displayTextAccept: boolean = false;
  excelAddressAccept: boolean = false;
  displayTypeAccept: boolean = false;
  isInputAccept: boolean = false;
  dataTypeCodeAccept: boolean = false;
  multiYearTimeAccept: boolean = false;
  timeRowOffsetAccept: boolean = false;
  timeColOffsetAccept: boolean = false;

  // -------------- Replacement values --------------

  //new: string | null = null;
  //newExcelAddress: string | null = null;
  //newDisplayType: string | null = null;
  //newMultiYearTime: boolean | null = null;
  //newTimeRowOffset: number | null = null;
  //newTimeColOffset: number | null = null;

}

export class ModelValueCompareOld {

  // ------------- Core Identifiers -------------
  /**
   * Unique identifier from ModelValue
   */
  id?: string | null;

  /**
   * Variable name - unique identifier used in both ModelValue and ExcelModel
   */
  varName?: string | null;

  /**
   * Code to identify the model value within a deal type
   */
  code?: string | null;

  // ------------- Hierarchical Structure -------------
  /**
   * Parent identifier for hierarchical structures
   */
  parentId?: string | null;

  /**
   * Row number from Excel (for ordering/hierarchy)
   */
  rowNo?: number | null;

  /**
   * Parent row number from Excel (for hierarchy)
   */
  parentRowNo?: number | null;

  // ------------- Association Identifiers -------------
  /**
   * Deal group identifier
   */
  dealGroupId?: string | null;

  /**
   * Deal type identifier
   */
  dealTypeId?: string | null;

  // ------------- Data Type & Control -------------
  /**
   * Code for the type of data to be stored
   */
  dataTypeCode?: string | null;

  /**
   * Code for the control type used for UI rendering
   */
  controlCode?: string | null;

  /**
   * Question type from Excel
   */
  questionType?: string | null;

  // ------------- Display Properties -------------
  /**
   * Display text - may be question text or output text
   */
  displayText?: string | null;

  /**
   * Question text from Excel
   */
  questionText?: string | null;

  /**
   * Whether to apply formatting/value substitution for display text
   */
  useDisplayFormat?: boolean | null;

  /**
   * Display sort order
   */
  sortOrder?: number | null;

  // ------------- Units & Formatting -------------
  /**
   * Units code
   */
  unitCode?: string | null;

  /**
   * Display text override for units (from ModelValue)
   */
  unitDisplay?: string | null;

  /**
   * Units from Excel
   */
  units?: string | null;

  /**
   * Format string for value display
   */
  formatValue?: string | null;

  // ------------- Value Properties -------------
  /**
   * Default value
   */
  defaultValue?: string | null;

  /**
   * Placeholder text in the UI
   */
  placeholder?: string | null;

  /**
   * Help comments or additional information
   */
  helpComments?: string | null;

  // ------------- Validation & Constraints -------------
  /**
   * Minimum value
   */
  min?: number | null;

  /**
   * Maximum value
   */
  max?: number | null;

  /**
   * Data size / max length
   */
  size?: number | null;

  /**
   * Question size from Excel
   */
  questionSize?: string | null;

  // ------------- Boolean Flags -------------
  /**
   * Whether the model value is an input
   */
  isInput?: boolean | null;

  /**
   * Whether the model value is used for a report
   */
  isReport?: boolean | null;

  /**
   * Whether to show output value to user
   */
  showToUser?: boolean | null;

  /**
   * Whether changes affect calculations
   */
  isPriceDriver?: boolean | null;

  /**
   * Whether the value is required
   */
  isRequired?: boolean | null;

  /**
   * Whether the value is readonly after initialization
   */
  isReadOnly?: boolean | null;

  /**
   * Whether control is enabled (from Excel)
   */
  isEnabled?: boolean | null;

  /**
   * Whether the value is metadata/structural
   */
  isMetaData?: boolean | null;

  /**
   * Whether the value is virtual (no storage, lookup only)
   */
  isVirtual?: boolean | null;

  /**
   * Whether the value is active
   */
  isActive?: boolean | null;

  /**
   * Whether the value can be overridden
   */
  override?: boolean | null;

  // ------------- Dependencies & Options -------------
  /**
   * Code map for matching and mapping values
   */
  codeMap?: string | null;

  /**
   * Whether value assignment updates parent deal header
   */
  updatesHeader?: boolean | null;

  /**
   * Whether the value assignment comes from an option list
   */
  hasOptions?: boolean | null;

  /**
   * Whether config supports option cascade values
   */
  hasOptionCascade?: boolean | null;

  /**
   * Whether config has dependencies with others
   */
  hasDependencies?: boolean | null;

  /**
   * Whether config has additional chart config
   */
  hasChart?: boolean | null;

  // ------------- Time Segments -------------
  /**
   * Whether the model value supports time segments
   */
  supportsTimeSegments?: boolean | null;

  /**
   * Multi-year time support from Excel
   */
  multiYearTime?: boolean | null;

  /**
   * Time row offset for multi-year data
   */
  timeRowOffset?: number | null;

  /**
   * Time column offset for multi-year data
   */
  timeColOffset?: number | null;

  // ------------- Excel Integration -------------
  /**
   * Whether the model value has Excel support
   */
  hasExcel?: boolean | null;

  /**
   * Excel cell address
   */
  excelAddress?: string | null;

  /**
   * Row offset/repeat for multi-year Excel data
   */
  excelRepeatRow?: number | null;

  /**
   * Column offset/repeat for multi-year Excel data
   */
  excelRepeatCol?: number | null;

  /**
   * Row starting point for grid data
   */
  gridRowStart?: number | null;

  /**
   * Column to determine if the row has valid grid data
   */
  gridColValid?: string | null;

  // ------------- Formula & Calculations -------------
  /**
   * Formula calculation
   */
  formula?: string | null;

  /**
   * Formula processing order
   */
  formulaOrder?: number | null;

  // ------------- Virtual Lookup -------------
  /**
   * Variable name used for virtual lookup
   */
  varNameVirt?: string | null;

  // ------------- Permissions -------------
  /**
   * Whether permission check is required
   */
  permissionCheck?: boolean | null;

  
  /**
   * SQL option link, if using options then specify a database table name to allow data to be sync'd from the master to the options.
   */
  sqlOptionLink?: string | null;

  // ------------- SQL Options (Future) -------------

  /**
   * SQL option lookup function (not implemented)
   */
  sqlOptionLookUpFn?: string | null;

  // ------------- Audit Fields -------------
  /**
   * Creation date and time
   */
  createdOn?: Date | null;

  /**
   * Creator's name
   */
  createdBy?: string | null;

  /**
   * Last modification date and time
   */
  lastModifiedOn?: Date | null;

  /**
   * Name of person who last modified
   */
  lastModifiedBy?: string | null;

  // ------------- Categories -------------
  /**
   * Categories that the model value belongs to
   */
  categories?: string[] | null;

  // ------------- Comparison State -------------
  /**
   * Whether this item exists in database but not in Excel
   */
  isInDbOnly?: boolean | null;

  /**
   * Whether this item exists in Excel but not in database
   */
  isInExcelOnly?: boolean | null;

  /**
   * Whether this item exists in both and has differences
   */
  hasDifferences?: boolean | null;

  /**
   * Whether this item is new (for UI highlighting)
   */
  isNew?: boolean | null;

  /**
   * Whether this item is selected/checked (for UI)
   */
  checked?: boolean | null;

  /**
   * Whether this item is highlighted (for UI)
   */
  highlighted?: boolean | null;
}
